import React from 'react'
import { Icon } from './Icon'
import { useAccordion } from './useAccordion'
import { NavChild, NavItem } from './types'
import { isOwnSiteUrl } from 'lib/url'

type Props = {
  items: NavItem[]
  isTop?: boolean
  isFooter?: boolean
}

export const delayStyle = (ms: number): React.CSSProperties => ({ ['--delay' as string]: `${ms}ms` })

const blankAttrs = (isBlank?: boolean, href?: string): { target?: string; rel?: string } => {
  if (!isBlank) return {}
  return { target: '_blank', rel: href && isOwnSiteUrl(href) ? 'noopener' : 'noopener noreferrer' }
}

const SiteMenuChild: React.FC<{ child: NavChild; isLast: boolean; isFooter: boolean }> = ({
  child,
  isLast,
  isFooter,
}) => {
  const isAccordion = !!child.title && child.links.length > 0
  const { isOpen, toggle, targetRef, initialStyle } = useAccordion(false)

  return (
    <div className={`c-site-menu__children--item ${isLast ? '--last' : ''}`}>
      {child.title && (
        <button
          type="button"
          className={`c-site-menu__button --border c-effect__accordion${isOpen ? ' is-show' : ''}`}
          onClick={toggle}
        >
          <p className="--text c-site-menu__button--text font-sans">{child.title}</p>
          <div className="--icon c-icon__accordion" />
        </button>
      )}
      <div
        ref={isAccordion ? targetRef : undefined}
        style={isAccordion ? initialStyle : undefined}
        className="c-site-menu__children--nav"
      >
        <div className="c-site-menu__children--list">
          {child.links.map((link, n) => (
            <div className="c-site-menu__children--item" key={link.href}>
              <a
                href={link.href}
                className={`c-site-menu__children--link ${n === child.links.length - 1 ? '--last' : ''}`}
                {...blankAttrs(link.isBlank, link.href)}
              >
                <p className="--text c-site-menu__children--text">{link.title}</p>
                {!isFooter && (
                  <div className="c-site-menu__icon">
                    <Icon name={link.isBlank ? 'external' : 'arrow'} viewBox="0 0 16 16" className="c-icon__full" />
                  </div>
                )}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const withParentLink = (item: NavItem): NavChild[] => {
  const children = item.children || []
  const firstHref = children[0]?.links[0]?.href
  if (!item.href || children.length === 0 || firstHref === item.href) return children

  const [first, ...rest] = children
  return [
    {
      ...first,
      links: [{ href: item.href, title: item.title, isBlank: item.isBlank }, ...first.links],
    },
    ...rest,
  ]
}

const SiteMenuItem: React.FC<{ item: NavItem; index: number; isFooter: boolean }> = ({ item, index, isFooter }) => {
  const children = withParentLink(item)
  const { isOpen, toggle, targetRef, initialStyle } = useAccordion(item.title === 'Services' && !isFooter)

  return (
    <li className="--item c-site-menu__item" style={delayStyle((index + 1) * 50)}>
      <div className="c-site-menu__item--inner">
        {children.length > 0 ? (
          <>
            <button
              type="button"
              className={`c-site-menu__button c-effect__accordion${isOpen ? ' is-show' : ''}`}
              onClick={toggle}
            >
              <p className="c-site-menu__text font-upgrade">{item.title}</p>
              <div className="--icon c-icon__accordion" />
            </button>
            <div
              ref={targetRef}
              style={initialStyle}
              className={`c-site-menu__children ${children.length === 1 ? '--single' : ''}`}
            >
              <div className="c-site-menu__children--inner">
                {children.map((child, j) => (
                  <SiteMenuChild
                    key={child.title || j}
                    child={child}
                    isLast={j === children.length - 1}
                    isFooter={isFooter}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <a
            href={item.href}
            className={`c-site-menu__link ${item.isBlank && !isFooter ? '--has-external' : ''}`}
            {...blankAttrs(item.isBlank, item.href)}
          >
            {item.isBlank && !isFooter ? (
              <span className="c-site-menu__link-inner">
                <p className="--text c-site-menu__text font-upgrade">{item.title}</p>
                <div className="c-site-menu__icon">
                  <Icon name="external" viewBox="0 0 16 16" className="c-icon__full" />
                </div>
              </span>
            ) : (
              <p className="--text c-site-menu__text font-upgrade">{item.title}</p>
            )}
          </a>
        )}
      </div>
    </li>
  )
}

const SiteMenu: React.FC<Props> = ({ items, isTop = false, isFooter = false }) => (
  <nav className={`c-site-menu ${isFooter ? '--footer' : ''}`}>
    <ul className="c-site-menu__list">
      {isTop && (
        <li className="--item c-site-menu__item" style={delayStyle(0)}>
          <a href="/" className="c-site-menu__link">
            <p className="--text c-site-menu__text font-upgrade">Top</p>
          </a>
        </li>
      )}
      {items.map((item, i) => (
        <SiteMenuItem key={item.title} item={item} index={i} isFooter={isFooter} />
      ))}
    </ul>
  </nav>
)

export { blankAttrs }
export default SiteMenu
